import { formatDateDisplay, formatDateTimeDisplay } from "@airange/shared";
import type { ProColumns } from "@ant-design/pro-components";
import { Tag } from "antd";
import type { ReactNode } from "react";
import { isValidElement } from "react";
import type { AdminRowActionItem } from "./admin-action-types";
import {
  ADMIN_ACTION_COLUMN_WIDTH_COMPACT,
  countVisibleAdminRowActions,
  resolveAdminActionColumnWidth,
} from "./admin-action-width";
import { type AuditActorDetailItem, AuditActorText } from "./audit-actor-text";

type ValueResolver<T> = (record: T) => unknown;

type BaseColumnOptions<T> = Omit<ProColumns<T>, "render" | "title"> & {
  title: ProColumns<T>["title"];
};

type TextColumnOptions<T> = BaseColumnOptions<T> & {
  getValue?: ValueResolver<T>;
  emptyText?: string;
};

type DateTimeColumnOptions<T> = BaseColumnOptions<T> & {
  getValue?: ValueResolver<T>;
  withSeconds?: boolean;
  emptyText?: string;
};

type DateColumnOptions<T> = BaseColumnOptions<T> & {
  getValue?: ValueResolver<T>;
  emptyText?: string;
};

export type AuditActorColumnOptions<T> = BaseColumnOptions<T> & {
  getActorId?: (record: T) => string | number | null | undefined;
  getUsername?: (record: T, actorId: string | null) => string | null | undefined;
  getRealName?: (record: T, actorId: string | null) => string | null | undefined;
  getDetailItems?: (record: T, actorId: string | null) => AuditActorDetailItem[] | undefined;
  fallback?: string;
};

export type AuditColumnsTitles<T = unknown> = {
  createdBy: ProColumns<T>["title"];
  createdAt: ProColumns<T>["title"];
  updatedBy: ProColumns<T>["title"];
  updatedAt: ProColumns<T>["title"];
};

export type AuditColumnsOptions<T> = {
  titles: AuditColumnsTitles<T>;
  createdBy?: Partial<Omit<AuditActorColumnOptions<T>, "title" | "dataIndex">>;
  createdAt?: Partial<Omit<DateTimeColumnOptions<T>, "title" | "dataIndex">>;
  updatedBy?: Partial<Omit<AuditActorColumnOptions<T>, "title" | "dataIndex">>;
  updatedAt?: Partial<Omit<DateTimeColumnOptions<T>, "title" | "dataIndex">>;
};

type TagColumnOptions<T> = BaseColumnOptions<T> & {
  getValue?: ValueResolver<T>;
  getLabel: (record: T) => ReactNode;
  getColor?: (record: T) => string | undefined;
};

export function resolveAdminActionsColumnWidthFromActions(actions: AdminRowActionItem[]) {
  return resolveAdminActionColumnWidth(countVisibleAdminRowActions(actions));
}

export function resolveAdminActionsColumnWidthFromRecords<T>(
  records: readonly T[],
  getActions: (record: T) => AdminRowActionItem[],
) {
  const maxVisibleActionCount = records.reduce(
    (maxCount, record) => Math.max(maxCount, countVisibleAdminRowActions(getActions(record))),
    0,
  );
  return resolveAdminActionColumnWidth(maxVisibleActionCount);
}

function readColumnValue<T>(record: T, dataIndex: ProColumns<T>["dataIndex"]) {
  if (!dataIndex) return undefined;
  if (Array.isArray(dataIndex)) {
    return dataIndex.reduce<unknown>((current, key) => {
      if (current == null || typeof current !== "object") return undefined;
      return (current as Record<string, unknown>)[String(key)];
    }, record);
  }
  return (record as Record<string, unknown>)[String(dataIndex)];
}

function stringifyStructuredTextValue(value: object) {
  if (Array.isArray(value)) {
    if (value.length === 0) return "";
  } else if (Object.keys(value).length === 0) {
    return "";
  }
  try {
    return JSON.stringify(value).trim();
  } catch {
    return String(value).trim();
  }
}

function resolveTextValue(value: unknown, emptyText: string): ReactNode {
  if (value == null || typeof value === "boolean") return emptyText;
  if (typeof value === "string") {
    const text = value.trim();
    return text || emptyText;
  }
  if (typeof value === "number" || typeof value === "bigint") {
    const text = String(value).trim();
    return text || emptyText;
  }
  if (isValidElement(value)) {
    return value;
  }
  if (Array.isArray(value)) {
    const text = stringifyStructuredTextValue(value);
    return text || emptyText;
  }
  if (typeof value === "object") {
    const text = stringifyStructuredTextValue(value);
    return text || emptyText;
  }
  const text = String(value).trim();
  return text || emptyText;
}

function normalizeAuditActorId(value: unknown): string | null {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
}

type AdminDateTimeFormatOptions = {
  withSeconds?: boolean;
  emptyText?: string;
};

type AdminDateFormatOptions = {
  emptyText?: string;
};

export function formatAdminDate(value?: string | null, options?: AdminDateFormatOptions) {
  const emptyText = options?.emptyText ?? "-";
  if (!value) {
    return emptyText;
  }
  return formatDateDisplay(value, emptyText);
}

export function formatAdminDateTime(value?: string | null, options?: AdminDateTimeFormatOptions) {
  const emptyText = options?.emptyText ?? "-";
  if (!value) {
    return emptyText;
  }
  return formatDateTimeDisplay(value, emptyText);
}

export type AdminListColumnSchema<T> = ProColumns<T>;

export function buildAdminListColumns<T>(schemas: AdminListColumnSchema<T>[]) {
  return schemas;
}

export function buildTextColumn<T>(options: TextColumnOptions<T>): ProColumns<T> {
  const { getValue, emptyText = "-", ...column } = options;
  const result: ProColumns<T> = {
    ...column,
    render: (_value, record) =>
      resolveTextValue(
        getValue ? getValue(record) : readColumnValue(record, column.dataIndex),
        emptyText,
      ),
  };
  return result;
}

export function buildDateTimeColumn<T>(options: DateTimeColumnOptions<T>): ProColumns<T> {
  const { getValue, withSeconds: _withSeconds = false, emptyText = "-", ...column } = options;
  const result: ProColumns<T> = {
    ...column,
    render: (_value, record) => {
      const value = getValue ? getValue(record) : readColumnValue(record, column.dataIndex);
      return formatAdminDateTime(value ? String(value) : null, { emptyText });
    },
  };
  return result;
}

export function buildDateColumn<T>(options: DateColumnOptions<T>): ProColumns<T> {
  const { getValue, emptyText = "-", ...column } = options;
  const result: ProColumns<T> = {
    ...column,
    render: (_value, record) => {
      const value = getValue ? getValue(record) : readColumnValue(record, column.dataIndex);
      return formatAdminDate(value ? String(value) : null, { emptyText });
    },
  };
  return result;
}

export function buildAuditActorColumn<T>(options: AuditActorColumnOptions<T>): ProColumns<T> {
  const { getActorId, getUsername, getRealName, getDetailItems, fallback, ...column } = options;
  const result: ProColumns<T> = {
    search: false,
    ...column,
    render: (_value, record) => {
      const actorId = normalizeAuditActorId(
        getActorId ? getActorId(record) : readColumnValue(record, column.dataIndex),
      );
      return (
        <AuditActorText
          actorId={actorId}
          username={getUsername?.(record, actorId) ?? null}
          realName={getRealName?.(record, actorId) ?? null}
          {...(fallback === undefined ? {} : { fallback })}
          {...(() => {
            const detailItems = getDetailItems?.(record, actorId);
            return detailItems === undefined ? {} : { detailItems };
          })()}
        />
      );
    },
  };
  return result;
}

export function buildAuditColumns<T>(options: AuditColumnsOptions<T>): ProColumns<T>[] {
  const { titles, createdBy, createdAt, updatedBy, updatedAt } = options;
  return [
    buildAuditActorColumn<T>({
      title: titles.createdBy,
      dataIndex: "created_by",
      ...createdBy,
    }),
    buildDateTimeColumn<T>({
      title: titles.createdAt,
      dataIndex: "created_at",
      search: false,
      ...createdAt,
    }),
    buildAuditActorColumn<T>({
      title: titles.updatedBy,
      dataIndex: "updated_by",
      ...updatedBy,
    }),
    buildDateTimeColumn<T>({
      title: titles.updatedAt,
      dataIndex: "updated_at",
      search: false,
      ...updatedAt,
    }),
  ];
}

export function buildAdminActionsColumn<T>(
  options: Omit<ProColumns<T>, "title" | "render"> & {
    title: ProColumns<T>["title"];
    render: NonNullable<ProColumns<T>["render"]>;
    actionCount?: number;
  },
): ProColumns<T> {
  const { key = "actions", width, actionCount, fixed = "right", ...column } = options;
  const result: ProColumns<T> = {
    key,
    width:
      width ??
      (actionCount === undefined
        ? ADMIN_ACTION_COLUMN_WIDTH_COMPACT
        : resolveAdminActionColumnWidth(actionCount)),
    fixed,
    ...column,
  };
  return result;
}

export function buildTagColumn<T>(options: TagColumnOptions<T>): ProColumns<T> {
  const { getLabel, getColor, ...column } = options;
  return {
    ...column,
    render: (_value, record) => {
      const color = getColor?.(record);
      return <Tag {...(color === undefined ? {} : { color })}>{getLabel(record)}</Tag>;
    },
  };
}
